import { Link, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../store/auth.ts';
import { notificationsApi } from '../features/notifications/api.ts';
import { Icon } from './Icon.tsx';

type Dest = { to: string; label: string; icon: string; matchPrefix?: string; showUnread?: boolean };

function navFor(role: 'SYS_ADMIN' | 'ORG_ADMIN' | 'USER'): Dest[] {
  if (role === 'SYS_ADMIN') {
    return [
      { to: '/admin/orgs', label: 'Orgs', icon: 'apartment', matchPrefix: '/admin' },
      { to: '/profile', label: 'Profile', icon: 'person' },
    ];
  }
  return [
    { to: '/dashboard', label: 'Docs', icon: 'description', matchPrefix: '/dashboard' },
    { to: '/inbox', label: 'Inbox', icon: 'inbox', showUnread: true },
    ...(role === 'ORG_ADMIN' ? [{ to: '/settings', label: 'Settings', icon: 'settings', matchPrefix: '/settings' }] : []),
    { to: '/profile', label: 'Profile', icon: 'person' },
  ];
}

// Material 3 navigation bar (mobile). Destinations only — the primary "Send"
// action is a FAB, sign-out lives in the top-bar menu. Active destination shows
// a tonal pill indicator behind a filled icon, with the label below.
export function BottomNav(): JSX.Element | null {
  const user = useAuthStore((s) => s.user);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { data: notifs } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationsApi.list(),
    enabled: !!user,
  });
  const unread = notifs?.items.filter((i) => !i.isRead).length ?? 0;

  if (!user) return null;
  const items = navFor(user.role);

  return (
    <nav
      aria-label="Primary"
      className="md:hidden fixed inset-x-0 bottom-0 z-40 flex items-stretch bg-[hsl(var(--chrome))]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {items.map((item) => {
        // Docs stays selected on document detail/new/edit screens too.
        const active = item.to === '/dashboard'
          ? (pathname.startsWith('/dashboard') || pathname.startsWith('/documents'))
          : item.matchPrefix ? pathname.startsWith(item.matchPrefix) : pathname === item.to;
        const showBadge = item.showUnread && unread > 0;
        return (
          <Link
            key={item.to}
            to={item.to}
            aria-label={item.label}
            aria-current={active ? 'page' : undefined}
            className="flex flex-1 flex-col items-center gap-1 pt-3 pb-2 active:scale-95 transition-transform"
          >
            <span
              className={[
                'relative grid h-8 w-16 place-items-center rounded-full transition-colors',
                active
                  ? 'bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]'
                  : 'text-muted-foreground',
              ].join(' ')}
            >
              <Icon name={item.icon} size={24} fill={active} />
              {showBadge && (
                <span
                  aria-label={`${unread} unread`}
                  className="absolute -top-0.5 right-2.5 grid h-4 min-w-4 place-items-center rounded-full bg-destructive px-1 text-[10px] font-semibold text-destructive-foreground tabular"
                >
                  {unread > 9 ? '9+' : unread}
                </span>
              )}
            </span>
            <span className={`text-[11px] leading-none ${active ? 'font-semibold text-ink' : 'text-muted-foreground'}`}>
              {item.label}
            </span>
          </Link>
        );
      })}
    </nav>
  );
}
